import { doc, type Firestore, type Transaction } from 'firebase/firestore';
import type { Expense, ExpenseSettlement } from '../types';

type SettlementChange = { tripId: string; expense: Expense; userId: string; next: ExpenseSettlement | null };
const financialBasis = (e: Expense) => JSON.stringify([
  e.amount, e.currency, e.targetAmount, e.targetCurrency, e.paid_by, [...e.split].sort(), e.extraSplitCount || 0,
]);
const settlementBasis = (s?: ExpenseSettlement) => JSON.stringify([
  s?.settled ?? 0, s?.settledOrig ?? null, (s as ExpenseSettlement & { full?: boolean })?.full ?? false,
  s?.pending?.kind, s?.pending?.amount, s?.pending?.markedBy, s?.pending?.markedAt,
]);

/** Compare the opened share before recording payment; never add two confirmations of the same payment. */
export async function writeSettlementChanges(transaction: Transaction, db: Firestore, changes: SettlementChange[]) {
  const writes = await Promise.all(changes.map(async ({ tripId, expense, userId, next }) => {
    const ref = doc(db, 'trips', tripId, 'expenses', expense.id);
    const snapshot = await transaction.get(ref);
    if (!snapshot.exists()) throw new Error('This expense was removed. Reopen Expenses to review your balances.');
    const current = snapshot.data() as Expense;
    if (financialBasis(current) !== financialBasis(expense)
      || settlementBasis(current.settlements?.[userId]) !== settlementBasis(expense.settlements?.[userId])) {
      throw new Error('This expense or settlement changed. Close this dialog and review the latest balance before trying again.');
    }
    if (!current.split.includes(userId) || current.paid_by === userId) throw new Error('This traveller does not owe a share of this expense.');
    const shareOrig = current.amount / (current.split.length + (current.extraSplitCount || 0));
    if (next && (!Number.isFinite(next.settled) || next.settled < 0 || !Number.isFinite(next.settledOrig)
      || next.settledOrig! < 0 || next.settledOrig! > shareOrig + 0.01)) {
      throw new Error('Enter a valid settlement amount within the outstanding share.');
    }
    const settlements = { ...current.settlements };
    if (next) settlements[userId] = next;
    else delete settlements[userId];
    return { ref, settlements };
  }));
  // Each group has one row per expense. Reject ambiguous duplicates rather than
  // applying two independently calculated cumulative records to the same document.
  if (new Set(writes.map(w => w.ref.path)).size !== writes.length) throw new Error('This selection contains duplicate expenses. Reopen Expenses.');
  for (const { ref, settlements } of writes) transaction.update(ref, { settlements });
}

export function writeSettlementChange(transaction: Transaction, db: Firestore, change: SettlementChange) {
  return writeSettlementChanges(transaction, db, [change]);
}
